import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PalestraAI } from '../components/ai/PalestraAI';
import { IstruzioniAI } from '../components/ai/IstruzioniAI';
import { NoteAgenzia } from '../components/ai/NoteAgenzia';
import { ConversazioniAI } from '../components/ai/ConversazioniAI';
import { RegoleCompagnie } from '../components/ai/RegoleCompagnie';
import { MediaRisorse } from '../components/ai/MediaRisorse';
import { Bot, Save, Send, Loader2, RotateCcw, Mail, Clock, MessageCircle, UserCheck, Moon, Dumbbell, Settings2, Activity, BookOpen, Clapperboard } from 'lucide-react';

const AZIONE_BADGE = {
  rispondi: { label: 'Risposta', cls: 'bg-emerald-100 text-emerald-700' },
  silenzio: { label: 'Silenzio', cls: 'bg-slate-100 text-slate-500' },
  operatore: { label: 'Operatore', cls: 'bg-amber-100 text-amber-700' },
  media: { label: 'Media inviato', cls: 'bg-violet-100 text-violet-700' },
  appuntamento: { label: 'Appuntamento', cls: 'bg-blue-100 text-blue-700' },
  errore_invio: { label: 'Errore invio', cls: 'bg-red-100 text-red-700' },
};

export default function ImpostazioniAI() {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'super_admin';
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [testMsg, setTestMsg] = useState('');
  const [testTel, setTestTel] = useState('');
  const [testNome, setTestNome] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const handleReport = async () => {
    setSendingReport(true);
    try {
      const r = await api.post('/ai-assistant/report/invia-ora');
      toast.success(`Report inviato (${r.data.stats?.totale ?? 0} messaggi analizzati)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio report'); }
    finally { setSendingReport(false); }
  };

  const loadAll = async () => {
    try {
      const [s, l, h] = await Promise.all([
        api.get('/ai-assistant/settings'),
        api.get('/ai-assistant/log'),
        api.get('/ai-assistant/handoffs'),
      ]);
      setSettings(s.data);
      setLogs(l.data.logs || []);
      setHandoffs(h.data.handoffs || []);
    } catch { toast.error('Errore caricamento impostazioni AI'); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleSave = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put('/ai-assistant/settings', patch || {
        delay_minutes: Number(settings.delay_minutes) || 10,
        faq_text: settings.faq_text || '',
        handoff_email: settings.handoff_email || undefined,
        fuori_orario_immediato: settings.fuori_orario_immediato !== false,
        orario_ufficio: settings.orario_ufficio || '09:00-13:00, 15:30-19:00',
        giorni_ufficio: settings.giorni_ufficio || [0, 1, 2, 3, 4],
        solo_numero_test: settings.solo_numero_test || '',
        numeri_test: settings.numeri_test || [],
        presentazione_servizi: settings.presentazione_servizi || '',
      });
      setSettings(r.data);
      toast.success('Impostazioni salvate');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (val) => {
    if (!isAdmin) { toast.error('Solo admin'); return; }
    try {
      const r = await api.put('/ai-assistant/settings', { enabled: val });
      setSettings(r.data);
      toast.success(val ? 'Assistente AI ATTIVO' : 'Assistente AI spento');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleTest = async () => {
    if (!testMsg.trim()) { toast.error('Scrivi un messaggio di prova'); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post('/ai-assistant/test', { message: testMsg, telefono: testTel || null, nome: testNome || null }, { timeout: 120000 });
      setTestResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore test AI'); }
    finally { setTesting(false); }
  };

  const handleRiattiva = async (chatId) => {
    try {
      await api.post('/ai-assistant/handoffs/riattiva', { chat_id: chatId });
      toast.success('AI riattivata per questa chat');
      loadAll();
    } catch { toast.error('Errore riattivazione'); }
  };

  if (!settings) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="impostazioni-ai-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-violet-100 text-violet-700"><Bot size={22} /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Assistente AI WhatsApp</h1>
            <p className="text-xs text-slate-500">Risponde ai clienti quando nessun operatore e' disponibile. Claude Sonnet 4.6</p>
          </div>
        </div>
        <div className="flex items-center gap-3 bg-white border rounded-xl px-4 py-2.5 shadow-sm">
          <span className={`text-sm font-semibold ${settings.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
            {settings.enabled ? 'ATTIVO' : 'SPENTO'}
          </span>
          <Switch checked={!!settings.enabled} onCheckedChange={handleToggle} disabled={!isAdmin} data-testid="ai-toggle" />
        </div>
      </div>

      <Tabs defaultValue="impostazioni" className="w-full">
        <TabsList data-testid="ai-tabs">
          <TabsTrigger value="impostazioni" data-testid="tab-impostazioni"><Settings2 size={13} className="mr-1.5" /> Impostazioni</TabsTrigger>
          <TabsTrigger value="istruzioni" data-testid="tab-istruzioni"><Settings2 size={13} className="mr-1.5" /> Istruzioni AI</TabsTrigger>
          <TabsTrigger value="note" data-testid="tab-note"><Settings2 size={13} className="mr-1.5" /> Note Agenzia</TabsTrigger>
          <TabsTrigger value="regole" data-testid="tab-regole"><BookOpen size={13} className="mr-1.5" /> Regole Compagnie</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media"><Clapperboard size={13} className="mr-1.5" /> Risorse / Media</TabsTrigger>
          <TabsTrigger value="conversazioni" data-testid="tab-conversazioni"><Settings2 size={13} className="mr-1.5" /> Conversazioni</TabsTrigger>
          <TabsTrigger value="palestra" data-testid="tab-palestra"><Dumbbell size={13} className="mr-1.5" /> Palestra AI</TabsTrigger>
          <TabsTrigger value="attivita" data-testid="tab-attivita"><Activity size={13} className="mr-1.5" /> Attivita'</TabsTrigger>
        </TabsList>

        <TabsContent value="istruzioni" className="mt-4">
          <IstruzioniAI isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="note" className="mt-4">
          <NoteAgenzia isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="regole" className="mt-4">
          <RegoleCompagnie isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="media" className="mt-4">
          <MediaRisorse isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="conversazioni" className="mt-4">
          <ConversazioniAI />
        </TabsContent>

        <TabsContent value="impostazioni" className="mt-4">
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Clock size={15} /> Comportamento</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Interviene se nessun operatore risponde entro (minuti)</Label>
              <Input type="number" min={1} max={240} value={settings.delay_minutes}
                onChange={e => setSettings(s => ({ ...s, delay_minutes: e.target.value }))}
                className="mt-1 w-32" data-testid="ai-delay-input" disabled={!isAdmin} />
            </div>
            <div className="border rounded-lg p-3 bg-violet-50/40 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5"><Moon size={13} /> Fuori orario rispondi subito</Label>
                <Switch checked={settings.fuori_orario_immediato !== false}
                  onCheckedChange={v => setSettings(s => ({ ...s, fuori_orario_immediato: v }))}
                  disabled={!isAdmin} data-testid="ai-fuori-orario-toggle" />
              </div>
              <p className="text-[11px] text-slate-500">Quando l'agenzia e' chiusa, l'AI risponde immediatamente senza aspettare i minuti di attesa.</p>
              <div>
                <Label className="text-[11px]">Orario ufficio (fasce separate da virgola)</Label>
                <Input value={settings.orario_ufficio || ''} placeholder="09:00-13:00, 15:30-19:00"
                  onChange={e => setSettings(s => ({ ...s, orario_ufficio: e.target.value }))}
                  className="mt-1 text-sm" data-testid="ai-orario-input" disabled={!isAdmin} />
              </div>
              <div>
                <Label className="text-[11px]">Giorni di apertura</Label>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((g, i) => {
                    const attivo = (settings.giorni_ufficio || [0, 1, 2, 3, 4]).includes(i);
                    return (
                      <button key={g} type="button" disabled={!isAdmin} data-testid={`ai-giorno-${i}`}
                        onClick={() => setSettings(s => {
                          const cur = s.giorni_ufficio || [0, 1, 2, 3, 4];
                          return { ...s, giorni_ufficio: attivo ? cur.filter(x => x !== i) : [...cur, i].sort() };
                        })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${attivo ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-400 border-slate-200'}`}>
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Modalita' test: rispondi SOLO a questo numero (vuoto = tutti)</Label>
                <Input value={(settings.numeri_test || []).join(', ') || settings.solo_numero_test || ''} placeholder="es. 3921234567, 3339876543"
                  onChange={e => setSettings(s => ({ ...s, numeri_test: e.target.value.split(',').map(x => x.trim()).filter(Boolean), solo_numero_test: '' }))}
                  className="mt-1 text-sm" data-testid="ai-numero-test-input" disabled={!isAdmin} />
              </div>
            </div>
            <div className="border rounded-lg p-3 bg-blue-50/50 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold flex items-center gap-1.5"><Clock size={13} /> Sofia può fissare appuntamenti</Label>
                <Switch checked={settings.appuntamenti_ai_enabled !== false}
                  onCheckedChange={v => setSettings(s => ({ ...s, appuntamenti_ai_enabled: v }))}
                  disabled={!isAdmin} data-testid="ai-appuntamenti-toggle" />
              </div>
              <p className="text-[11px] text-slate-500">Se attivo, Sofia può prenotare appuntamenti in agenzia con Antonino o Samuele (slot da 1 ora, negli orari ufficio, almeno 2 giorni di anticipo), controllando la disponibilità in agenda e notificando il consulente.</p>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail size={12} /> Email agenzia per richieste operatore</Label>
              <Input type="email" placeholder="agenzia@esempio.it" value={settings.handoff_email || ''}
                onChange={e => setSettings(s => ({ ...s, handoff_email: e.target.value }))}
                className="mt-1" data-testid="ai-handoff-email-input" disabled={!isAdmin} />
            </div>
            <div>
              <Label className="text-xs">Presentazione servizi (l'AI si presenta come assistente virtuale e descrive questi servizi al primo messaggio)</Label>
              <Textarea rows={4} placeholder={"Es.\nASSICURAZIONI: auto, moto, casa, vita...\nFINANZIAMENTI: prestiti personali, cessione del quinto..."}
                value={settings.presentazione_servizi || ''}
                onChange={e => setSettings(s => ({ ...s, presentazione_servizi: e.target.value }))}
                className="mt-1 text-sm" data-testid="ai-presentazione-textarea" disabled={!isAdmin} />
            </div>
            <div>
              <Label className="text-xs">FAQ dell'agenzia (orari, indirizzo, servizi, come pagare...)</Label>
              <Textarea rows={8} placeholder={"Es.\nOrari: lun-ven 9:00-13:00 / 15:30-19:00\nIndirizzo: ...\nPer pagare la polizza: ..."}
                value={settings.faq_text || ''}
                onChange={e => setSettings(s => ({ ...s, faq_text: e.target.value }))}
                className="mt-1 text-sm" data-testid="ai-faq-textarea" disabled={!isAdmin} />
            </div>
            {isAdmin && (
              <Button onClick={() => handleSave()} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-save-btn">
                {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Salva impostazioni
              </Button>
            )}
            <div className="text-[11px] text-slate-400 leading-relaxed border-t pt-3">
              Sicurezza: prima di comunicare dati di polizza, l'AI chiede al cliente <b>targa e codice fiscale</b> e li incrocia con il numero di cellulare nel gestionale. Se il cliente chiede un operatore, l'AI si ferma e invia email + notifica al team.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><MessageCircle size={15} /> Prova l'AI (non invia nulla)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Nome cliente (opz.)" value={testNome} onChange={e => setTestNome(e.target.value)} data-testid="ai-test-nome" />
              <Input placeholder="Cellulare cliente (opz.)" value={testTel} onChange={e => setTestTel(e.target.value)} data-testid="ai-test-tel" />
            </div>
            <Textarea rows={3} placeholder="Scrivi come se fossi il cliente. Es: 'Buongiorno, quando scade la mia assicurazione?'"
              value={testMsg} onChange={e => setTestMsg(e.target.value)} data-testid="ai-test-message" />
            <Button onClick={handleTest} disabled={testing} variant="outline" className="border-violet-300 text-violet-700" data-testid="ai-test-btn">
              {testing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} Prova risposta AI
            </Button>
            {testResult && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="ai-test-result">
                <div className="flex items-center gap-2">
                  <Badge className={`${(AZIONE_BADGE[testResult.azione] || AZIONE_BADGE.silenzio).cls} border-0 text-[10px]`}>
                    {(AZIONE_BADGE[testResult.azione] || AZIONE_BADGE.silenzio).label}
                  </Badge>
                  {testResult.oggetto && <span className="text-xs text-amber-700 font-medium">Oggetto: {testResult.oggetto}</span>}
                  <span className="text-[10px] text-slate-400 ml-auto">{testResult.stato_verifica}</span>
                </div>
                {testResult.risposta
                  ? <p className="text-sm whitespace-pre-wrap text-slate-700">{testResult.risposta}</p>
                  : <p className="text-xs italic text-slate-400">L'AI ha scelto di non rispondere.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="palestra" className="mt-4">
          <PalestraAI />
        </TabsContent>

        <TabsContent value="attivita" className="mt-4 space-y-6">
      {handoffs.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><UserCheck size={15} /> Chat in attesa di operatore ({handoffs.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {handoffs.map(h => (
                <div key={h.chat_id} className="flex items-center justify-between border rounded-lg px-3 py-2 bg-amber-50/50" data-testid={`handoff-row-${h.chat_id}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{h.chat_name || h.chat_id}</p>
                    <p className="text-[11px] text-slate-500">{h.handoff_oggetto || 'Richiesta operatore'} · {h.handoff_at ? new Date(h.handoff_at).toLocaleString('it-IT') : ''}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleRiattiva(h.chat_id)} data-testid={`riattiva-btn-${h.chat_id}`}>
                    <RotateCcw size={13} className="mr-1" /> Riattiva AI
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Attivita' recente dell'AI</CardTitle>
            <Button size="sm" variant="outline" onClick={handleReport} disabled={sendingReport} data-testid="ai-report-btn">
              {sendingReport ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Mail size={13} className="mr-1" />} Invia report ora
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">Ogni lunedi' alle 8:00 ricevi via email il riepilogo settimanale: messaggi gestiti, richieste operatore e analisi di cosa chiedono i clienti.</p>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? <p className="text-xs text-slate-400 italic">Nessuna attivita' registrata.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ai-log-table">
                <thead><tr className="text-left text-slate-400 border-b">
                  <th className="py-1.5 pr-3">Data</th><th className="py-1.5 pr-3">Cliente</th><th className="py-1.5 pr-3">Azione</th><th className="py-1.5">Testo / Oggetto</th>
                </tr></thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">{new Date(l.created_at).toLocaleString('it-IT')}</td>
                      <td className="py-1.5 pr-3 font-medium text-slate-700">{l.cliente || l.chat_id}</td>
                      <td className="py-1.5 pr-3"><Badge className={`${(AZIONE_BADGE[l.azione] || AZIONE_BADGE.silenzio).cls} border-0 text-[10px]`}>{(AZIONE_BADGE[l.azione] || AZIONE_BADGE.silenzio).label}</Badge></td>
                      <td className="py-1.5 text-slate-600 max-w-md truncate">{l.oggetto ? `[${l.oggetto}] ` : ''}{l.testo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
